import { Button, Icon, TextButton, Trigger, isSuccess, useLaravelCrudEntity, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Address } from "../../../../shared/ui/Address";
import { addressService } from "../../shared/api/addressService";
import { contactService } from "../../shared/api/contactService";
import { useDefaultCountry } from "../../shared/hooks";
import { AddressFormState, IContactUpdateResource } from "../../shared/types";
import {
	DrawerFormButtonContainer,
	MarkAsPrimaryButton,
	PrimaryLabel,
	ViewPageSection,
	ViewPageSectionBody,
	ViewPageSectionHeader,
	ViewPageSectionItem,
	ViewPageSectionListItem,
} from "../../shared/view";
import { ValueWrapper } from "../../shared/view/ViewComponents";
import { ContactAddressDrawer } from "./ContactAddressDrawer";

const StyledViewPageSection = styled(ViewPageSection)`
	grid-area: address;
`;

const StyledPrimaryLabel = styled(PrimaryLabel)`
	align-self: center;
`;

export const ContactViewAddressSection: FC<{ contact: IContactUpdateResource }> = ({ contact }) => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const { defaultCountry } = useDefaultCountry();
	const crudOperations = useLaravelCrudEntity(addressService, {
		entityName: t("Address"),
		messages: {
			create: t("Address was saved."),
			update: t("Address was saved."),
			delete: t("Address was deleted."),
		},
	});

	const emptyAddress: AddressFormState = useMemo(
		() => ({
			type: "work",
			city: "",
			street: "",
			houseNumber: "",
			zip: "",
			additionalInfo: "",
			country: defaultCountry || null,
		}),
		[defaultCountry],
	);

	const onClosed = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: contactService.listKey });
		queryClient.invalidateQueries({ queryKey: contactService.showKey(contact.id) });
	}, [contact.id, queryClient]);

	return (
		<StyledViewPageSection>
			<ViewPageSectionHeader>{t("Addresses")}</ViewPageSectionHeader>
			<ViewPageSectionBody>
				{contact.addresses.map((address) => (
					<Trigger
						key={address.id}
						render={(props) => (
							<ContactAddressDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Edit address")}
								initialValues={address}
								onClosed={onClosed}
								onSubmit={({ country, ...values }) => {
									if (!country) throw "Missing validation for field: country";

									return crudOperations.updateEntity(address.id, {
										...values,
										contactId: contact.id,
										countryId: country.id,
									});
								}}
							>
								{!address.isPrimary && (
									<DrawerFormButtonContainer>
										<MarkAsPrimaryButton
											entityId={address.id}
											markAsPrimary={addressService.markAsPrimary}
											onSubmit={props.handleClose}
										/>
										<Button
											type="button"
											color="danger"
											onClick={async () => {
												const res = await contactService.deleteAddress(contact.id, address.id);
												if (isSuccess(res)) {
													showToast({
														body: t("Address was deleted."),
														title: t("Address"),
														type: "success",
													});
													props.handleClose();
												}
											}}
										>
											{t("Delete address")}
										</Button>
									</DrawerFormButtonContainer>
								)}
							</ContactAddressDrawer>
						)}
					>
						{(props) => (
							<ViewPageSectionListItem {...props}>
								<ValueWrapper>
									<Address address={address} />
									{address.isPrimary && <StyledPrimaryLabel />}
								</ValueWrapper>
							</ViewPageSectionListItem>
						)}
					</Trigger>
				))}
				<ViewPageSectionItem>
					<Trigger
						render={(props) => (
							<ContactAddressDrawer
								open={props.open}
								onClose={props.handleClose}
								title={t("Add address")}
								initialValues={emptyAddress}
								onClosed={onClosed}
								onSubmit={({ country, ...values }) => {
									if (!country) throw "Missing validation for field: country";

									return crudOperations.createEntity({
										...values,
										contactId: contact.id,
										countryId: country.id,
									});
								}}
							/>
						)}
					>
						{(props) => (
							<TextButton {...props} color="primary" prefix={<Icon $icon="plus" />}>
								{t("Add address")}
							</TextButton>
						)}
					</Trigger>
				</ViewPageSectionItem>
			</ViewPageSectionBody>
		</StyledViewPageSection>
	);
};
